import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, type StudiesResponse } from './client';

/**
 * Studies — the dashboard's read/write client for `GET/POST/PATCH/DELETE
 * /studies`.
 *
 * The row shape comes from `schema.d.ts`, so a field renamed on the API breaks
 * this build rather than showing up blank at runtime. The two unions below are
 * narrower than the generated `string`: OpenAPI does not carry the enums, and
 * the editor's selects need the exact members.
 */

/** `invitation` (055): an extension study that takes only people from the trials before it. */
export const STUDY_STATUSES = ['recruiting', 'active', 'development', 'completed', 'invitation'] as const;
export type StudyStatus = (typeof STUDY_STATUSES)[number];

export const QUESTION_TYPES = ['yesno', 'choice', 'checks', 'short', 'long', 'number'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

type WireStudy = StudiesResponse['studies'][number];

export type StudyQuestion = Omit<WireStudy['questions'][number], 'type'> & {
  type: QuestionType;
};

/**
 * The study page's primary button. `none` is "Learn more only": no form at
 * all (012) — ocd-repeated and k23-ocd carry it.
 */
export const STUDY_CTAS = ['intake', 'waitlist', 'subscribe', 'none'] as const;
export type StudyCta = (typeof STUDY_CTAS)[number];

/**
 * `boardEnrolled` is the API's live count of the study's records at Enrolled,
 * beside `enrolled`, the number staff keep by hand.
 */
export type Study = Omit<WireStudy, 'status' | 'cta' | 'questions'> & {
  status: StudyStatus;
  cta: StudyCta;
  questions: StudyQuestion[];
};

/**
 * The fields a create or update may set. Not `mailbox`: it is set up in
 * Microsoft 365, and the API refuses a body that names one (400). Not
 * `boardEnrolled`, which the API counts.
 */
export type StudyInput = Omit<Study, 'slug' | 'statusLabel' | 'sortOrder' | 'mailbox' | 'boardEnrolled'>;

export function useStudies() {
  return useQuery({
    queryKey: ['studies'],
    queryFn: () => request<{ studies: Study[] }>('/studies/'),
    select: (d) => d.studies,
  });
}

export function useCreateStudy() {
  const qc = useQueryClient();
  return useMutation({
    /**
     * `slug` is the study's address, fixed once the study exists; left out,
     * the API makes it from the title, and two studies cannot share one.
     */
    mutationFn: (body: Partial<StudyInput> & { label: string; slug?: string }) =>
      request<{ study: Study }>('/studies/', { method: 'POST', body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateStudy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, patch }: { slug: string; patch: Partial<StudyInput> }) =>
      request<{ study: Study }>(`/studies/${slug}`, { method: 'PATCH', body: patch }),
    /**
     * Write the saved row straight into the cache.
     *
     * Not an optimistic pre-write: the editor holds its own draft, so the only
     * thing the cache owes it is the truth as soon as the server states it —
     * which is what makes the "Unsaved changes" indicator flip to "Saved"
     * without waiting for a refetch. On failure the cache is left alone and the
     * draft keeps the edits, so nothing anyone typed is thrown away.
     */
    onSuccess: (res) => {
      qc.setQueryData<{ studies: Study[] }>(['studies'], (old) =>
        old
          ? { studies: old.studies.map((s) => (s.slug === res.study.slug ? res.study : s)) }
          : old,
      );
    },
    onSettled: () => invalidate(qc),
  });
}

export function useDeleteStudy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => request(`/studies/${slug}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * A study edit changes the study picker every form renders, so `/meta` — which
 * carries that list — has to go too.
 */
function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['studies'] });
  void qc.invalidateQueries({ queryKey: ['meta'] });
}
